import api, { apiEndpoints } from '../lib/api'
import { MetadataSchema } from '../config/metadataSchemas'
import {
  PROCESSING_MESSAGES,
  PROCESSING_STATUS,
  type ProcessingStatus,
} from '../constants/processingMessages'

import type {
  ChatSessionSource,
  ShareAccessLevel,
  UserMinimal,
} from '../types/agents'
import type { CompositeExecution } from '../types/metadata'

export interface ChatSessionUI {
  id: string
  title: string
  agentType: string
  messageCount: number
  timestamp: string
  status?: string
  source?: ChatSessionSource
  metadata?: Record<string, string>
  // Sharing fields
  isShared?: boolean
  isOwner?: boolean
  myAccessLevel?: ShareAccessLevel | 'owner' | null
  sharedBy?: UserMinimal | null
  participantsCount?: number
}

export interface MessageSender {
  id: number
  name: string
  username: string
}

export interface SystemMessageMetadata {
  action?: string
  actorId?: number
  actorName?: string
  targetName?: string
  targetType?: 'user' | 'group'
}

export interface ChatMessageUI {
  id: string
  type: 'user' | 'agent' | 'system'
  content: string
  timestamp: string
  sender?: MessageSender
  metadata?: SystemMessageMetadata
}

export interface SessionOwner {
  id: number
  username: string
  fullName: string
}

export interface ChatHistoryResponse {
  messages: ChatMessageUI[]
  metadataSchema: MetadataSchema | null
  agentMetadata: Record<string, unknown>
  progressPercentage: number
  owner?: SessionOwner
  isOwner?: boolean
  myAccessLevel?: string
}

const inferMessageType = (
  rawRole: unknown,
  senderData?: Record<string, unknown>
): 'user' | 'agent' | 'system' => {
  const normalizedRole = String(rawRole || '')
    .trim()
    .toLowerCase()

  if (normalizedRole === 'user' || normalizedRole === 'human') {
    return 'user'
  }

  if (normalizedRole === 'system') {
    return 'system'
  }

  if (normalizedRole === 'assistant' || normalizedRole === 'ai') {
    return senderData ? 'user' : 'agent'
  }

  // Defensive fallback: some legacy/inconsistent payloads may omit a
  // normalized user role but still include human sender metadata.
  if (senderData) {
    return 'user'
  }

  return 'agent'
}

export const chatApi = {
  async listSessions(agentType: string): Promise<ChatSessionUI[]> {
    const res = await api.get(apiEndpoints.chat.sessions, {
      params: { agent_type: agentType },
    })
    const results = (res.data?.results ?? []) as Record<string, unknown>[]
    return results.map(item => ({
      id: String(item.id),
      title: String(item.title || 'Nova Conversa'),
      agentType: String(item.agent_type || ''),
      messageCount: Number(item.message_count ?? item.total_messages ?? 0),
      timestamp: String(
        item.last_activity ||
          item.updated_at ||
          item.created_at ||
          new Date().toISOString()
      ),
      status: String(item.status || 'in_progress'),
      source: (item.source === 'scheduled'
        ? 'scheduled'
        : 'manual') as ChatSessionSource,
      metadata:
        item.metadata && typeof item.metadata === 'object'
          ? Object.entries(item.metadata as Record<string, unknown>).reduce<
              Record<string, string>
            >((acc, [key, value]) => {
              if (value === null || value === undefined) return acc
              const str = String(value).trim()
              if (!str) return acc
              acc[key] = str
              return acc
            }, {})
          : undefined,
      // Sharing fields
      isShared: Boolean(item.is_shared),
      isOwner: item.is_owner !== undefined ? Boolean(item.is_owner) : true,
      myAccessLevel: item.my_access_level
        ? (String(item.my_access_level) as ShareAccessLevel | 'owner')
        : null,
      sharedBy: item.shared_by
        ? {
            id: Number((item.shared_by as Record<string, unknown>).id),
            username: String(
              (item.shared_by as Record<string, unknown>).username || ''
            ),
            email: String(
              (item.shared_by as Record<string, unknown>).email || ''
            ),
            fullName: String(
              (item.shared_by as Record<string, unknown>).full_name ||
                (item.shared_by as Record<string, unknown>).fullName ||
                ''
            ),
          }
        : null,
      participantsCount: Number(item.participants_count ?? 1),
    }))
  },

  async getHistory(sessionId: string): Promise<ChatHistoryResponse> {
    const res = await api.get(apiEndpoints.chat.history(sessionId))
    const messages = (res.data?.messages ?? []) as Record<string, unknown>[]
    const metadataSchema = (res.data?.metadata_schema ||
      null) as MetadataSchema | null
    const agentMetadata = (res.data?.agent_metadata || {}) as Record<
      string,
      unknown
    >
    const progressPercentage = Number(res.data?.progress_percentage ?? 0)

    // Extract owner info from response
    const ownerData = res.data?.owner as Record<string, unknown> | undefined
    const owner: SessionOwner | undefined = ownerData
      ? {
          id: Number(ownerData.id),
          username: String(ownerData.username || ''),
          fullName: String(ownerData.full_name || ownerData.username || ''),
        }
      : undefined

    return {
      messages: messages.map((m, idx) => {
        // Extract sender info from message
        const senderData = m.sender as Record<string, unknown> | undefined
        const sender: MessageSender | undefined = senderData
          ? {
              id: Number(senderData.id),
              name: String(senderData.name || senderData.username || ''),
              username: String(senderData.username || ''),
            }
          : undefined

        const messageType = inferMessageType(m.role, senderData)

        // Extract system message metadata if applicable
        const metadataRaw = m.metadata as Record<string, unknown> | undefined
        const metadata: SystemMessageMetadata | undefined =
          messageType === 'system' && metadataRaw
            ? {
                action: metadataRaw.action
                  ? String(metadataRaw.action)
                  : undefined,
                actorId: metadataRaw.actor_id
                  ? Number(metadataRaw.actor_id)
                  : undefined,
                actorName: metadataRaw.actor_name
                  ? String(metadataRaw.actor_name)
                  : undefined,
                targetName: metadataRaw.target_name
                  ? String(metadataRaw.target_name)
                  : undefined,
                targetType: metadataRaw.target_type
                  ? (String(metadataRaw.target_type) as 'user' | 'group')
                  : undefined,
              }
            : undefined

        return {
          id: `${sessionId}-${idx}`,
          type: messageType,
          content: String(m.content ?? ''),
          timestamp: String(
            m.created_at || m.timestamp || new Date().toISOString()
          ),
          sender,
          metadata,
        }
      }),
      metadataSchema,
      agentMetadata,
      progressPercentage,
      owner,
      isOwner: Boolean(res.data?.is_owner ?? true),
      myAccessLevel: res.data?.my_access_level
        ? String(res.data.my_access_level)
        : undefined,
    }
  },

  /**
   * Send message with automatic polling for long-running operations.
   * This method handles operations that may exceed HTTP timeout (30s).
   *
   * Flow:
   * 1. Initiate processing (receive job_id)
   * 2. Wait for estimated time (5s optimistic)
   * 3. Poll for result with exponential backoff until ready
   * 4. Return complete response
   */
  async sendMessage(params: {
    agentType: string
    message: string
    sessionId?: string | null
    tool?: string
    toolParams?: Record<string, unknown>
    onProgress?: (status: string) => void
    onStatusChange?: (status: ProcessingStatus, startTime?: number) => void
    onToolProgress?: (toolProgress: Record<string, unknown> | null) => void
    onSessionCreated?: (sessionId: string, title?: string) => void
    onIntermediateResponse?: (
      response: string,
      agentMetadata?: Record<string, unknown>
    ) => void
    abortSignal?: AbortSignal
  }): Promise<{
    sessionId: string
    response: string
    title?: string
    agentMetadata?: Record<string, unknown>
  }> {
    const payload: Record<string, unknown> = {
      agent_type: params.agentType,
      message: params.message,
    }
    if (params.sessionId) payload.session_id = params.sessionId
    if (params.tool) payload.tool = params.tool
    if (params.toolParams) payload.tool_params = params.toolParams

    // 1. Initiate processing
    const initRes = await api.post(apiEndpoints.chat.sendMessage, payload)
    const sessionId = String(initRes.data?.session_id)
    const title = initRes.data?.title ? String(initRes.data.title) : undefined

    // Notify session created (for adding to list without setting as current)
    params.onSessionCreated?.(sessionId, title)

    // Notify sending status
    params.onProgress?.(PROCESSING_MESSAGES.SENDING)
    params.onStatusChange?.(PROCESSING_STATUS.SENDING, Date.now())

    // 2. Wait initial optimistic time (5s instead of 15s)
    await new Promise(resolve => setTimeout(resolve, 5000))

    // Transition to queued status (task is likely still in queue)
    // Polling will update to PROCESSING when worker starts
    params.onProgress?.(PROCESSING_MESSAGES.QUEUED)
    const processingStartTime = Date.now()
    params.onStatusChange?.(PROCESSING_STATUS.QUEUED, processingStartTime)

    // 3. Poll unified status endpoint (combines result + progress)
    // Adaptive polling with backend recommendation
    let attempts = 0
    const maxAttempts = 60 // Max 60 attempts (5 minutes with 5s backoff cap)
    let currentPollInterval = 2000 // Default 2s, backend will adjust
    let lastSeenMessageCount = 1 // 1 = user message already sent

    while (attempts < maxAttempts) {
      // Check if polling was aborted
      if (params.abortSignal?.aborted) {
        params.onToolProgress?.(null)
        throw new Error('Polling cancelled')
      }

      try {
        // Single unified API call for both result and progress
        // Extended timeout for long-running Playwright operations (120s)
        const statusRes = await api.get(
          `/api/agents/chat/sessions/${sessionId}/poll/`,
          { timeout: 120000 } // 2 minutes per poll request
        )

        const jobStatus = statusRes.data?.job_status
        // toolProgress disabled for now - will be re-enabled when UI is redesigned
        // const toolProgress = statusRes.data?.tool_progress
        const result = statusRes.data?.result
        const agentMetadata = statusRes.data?.agent_metadata
        const recommendedInterval = statusRes.data?.recommended_poll_interval_ms

        // Check if job completed FIRST (before checking poll interval)
        if (jobStatus === 'completed') {
          const data = result?.data || result
          const response = String(data?.response ?? '')
          const responseTitle = data?.title ? String(data.title) : title
          params.onToolProgress?.(null)

          // Check if this is a composite tool with more steps pending
          const compositeExecution = (
            agentMetadata as Record<string, unknown> | undefined
          )?.composite_execution as CompositeExecution | undefined
          const compositeRunning = compositeExecution?.status === 'running'

          if (compositeRunning) {
            // Composite com mais steps — entregar resposta e continuar polling
            params.onIntermediateResponse?.(
              response,
              agentMetadata as Record<string, unknown>
            )
            lastSeenMessageCount++

            const {
              current_step: currentStep,
              total_steps: totalSteps,
              steps,
            } = compositeExecution
            const stepLabel = steps?.[currentStep]?.label ?? ''

            if (stepLabel) {
              params.onProgress?.(
                `Etapa ${currentStep + 1}/${totalSteps}: ${stepLabel}...`
              )
            }
            params.onStatusChange?.(PROCESSING_STATUS.PROCESSING)

            await new Promise(resolve => setTimeout(resolve, 2000))
            attempts++
            continue
          }

          // Composite completo ou tool normal — entregar resposta final.
          // Buscar historico pra pegar mensagens intermediarias que o polling perdeu.
          if (lastSeenMessageCount > 1 && params.onIntermediateResponse) {
            try {
              const historyRes = await api.get(
                `/api/agents/chat/sessions/${sessionId}/history/`,
                { timeout: 10000 }
              )
              const allMessages = (historyRes.data?.messages ?? []) as Array<{
                content: string
              }>
              // Entregar mensagens intermediarias que ainda nao foram vistas
              // (exclui a primeira user msg e a ultima que sera o retorno final)
              for (
                let i = lastSeenMessageCount;
                i < allMessages.length - 1;
                i++
              ) {
                const msg = allMessages[i]
                if (
                  msg.content &&
                  !msg.content.startsWith('[system:composite]')
                ) {
                  params.onIntermediateResponse(
                    msg.content,
                    agentMetadata as Record<string, unknown>
                  )
                }
              }
            } catch {
              // Falha ao buscar historico nao bloqueia retorno final
            }
          }

          return {
            sessionId,
            response,
            title: responseTitle,
            agentMetadata: agentMetadata as Record<string, unknown>,
          }
        }

        // Check if job failed
        if (jobStatus === 'error') {
          // O backend manda a frase em `error`, no topo da resposta do poll
          // (`result` vem vazio na falha): lendo só `result.error`, a pessoa
          // via "Unknown error".
          const error =
            statusRes.data?.error ||
            result?.error ||
            'Não consegui processar sua mensagem. Tente de novo.'
          params.onToolProgress?.(null)
          throw new Error(String(error))
        }

        // Backend signals to STOP polling (null = no more polling needed)
        if (recommendedInterval === null) {
          throw new Error(
            'Perdi o acompanhamento desta resposta. Recarregue a conversa para ver se ela chegou.'
          )
        }

        // Update polling interval if backend recommends a different one
        if (
          recommendedInterval &&
          recommendedInterval !== currentPollInterval
        ) {
          currentPollInterval = recommendedInterval
        }

        // Update UI based on job status (pending = queued, started = processing)
        if (jobStatus === 'pending') {
          params.onStatusChange?.(PROCESSING_STATUS.QUEUED)
          params.onProgress?.(PROCESSING_MESSAGES.QUEUED)
        } else if (jobStatus === 'started') {
          params.onStatusChange?.(PROCESSING_STATUS.PROCESSING)
          params.onProgress?.(PROCESSING_MESSAGES.PROCESSING)
        }

        // Detectar mensagens intermediarias de tools compostas durante o polling.
        // Quando total_messages aumenta, buscar historico e entregar ao chat.
        const totalMessages = Number(statusRes.data?.total_messages ?? 0)
        if (
          totalMessages > lastSeenMessageCount &&
          params.onIntermediateResponse
        ) {
          try {
            const historyRes = await api.get(
              `/api/agents/chat/sessions/${sessionId}/history/`,
              { timeout: 10000 }
            )
            const allMessages = (historyRes.data?.messages ?? []) as Array<{
              content: string
              type?: string
            }>
            // Entregar apenas mensagens novas do assistant
            for (let i = lastSeenMessageCount; i < allMessages.length; i++) {
              const msg = allMessages[i]
              if (
                msg.content &&
                !msg.content.startsWith('[system:composite]')
              ) {
                params.onIntermediateResponse(
                  msg.content,
                  agentMetadata as Record<string, unknown>
                )
              }
            }
            lastSeenMessageCount = totalMessages
          } catch {
            // Falha ao buscar historico — nao bloqueia o polling
          }
        }

        // Still processing, wait before next poll (use recommended interval)
        const interval = recommendedInterval || currentPollInterval
        await new Promise(resolve => setTimeout(resolve, interval))
        attempts++
      } catch (error: unknown) {
        // If it's a 202 (still processing), continue polling
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: { status?: number } }).response?.status === 202
        ) {
          await new Promise(resolve => setTimeout(resolve, currentPollInterval))
          attempts++
          continue
        }

        // Other errors, throw
        params.onStatusChange?.(PROCESSING_STATUS.ERROR)
        throw error
      }
    }

    // Timeout after max attempts
    params.onStatusChange?.(PROCESSING_STATUS.ERROR)
    throw new Error(PROCESSING_MESSAGES.ERROR_TIMEOUT)
  },

  async updateSessionTitle(sessionId: string, title: string): Promise<void> {
    await api.patch(apiEndpoints.chat.updateSession(sessionId), { title })
  },

  async updateSessionStatus(sessionId: string, status: string): Promise<void> {
    await api.patch(apiEndpoints.chat.updateSession(sessionId), { status })
  },

  async getSessionWithMetadata(
    sessionId: string
  ): Promise<Record<string, unknown>> {
    const res = await api.get(`/api/agents/chat-sessions/${sessionId}/`)
    return res.data as Record<string, unknown>
  },

  async updateMetadataField(
    sessionId: string,
    field: string,
    value: unknown
  ): Promise<void> {
    await api.patch(
      `/api/agents/chat-sessions/${sessionId}/update_metadata_field/`,
      {
        field,
        value,
        validate: true,
      }
    )
  },

  /**
   * Resume polling for a session if there's an ongoing job.
   * Called when loading a session to recover from page refresh/navigation.
   *
   * Returns:
   * - null if no job is running
   * - { response, title } if job completes successfully
   */
  async resumePollingIfNeeded(params: {
    sessionId: string
    onProgress?: (status: string) => void
    onStatusChange?: (status: ProcessingStatus, startTime?: number) => void
    onToolProgress?: (toolProgress: Record<string, unknown> | null) => void
    abortSignal?: AbortSignal
  }): Promise<{
    response: string
    title?: string
    agentMetadata?: Record<string, unknown>
  } | null> {
    try {
      // Check if already aborted before starting
      if (params.abortSignal?.aborted) {
        // eslint-disable-next-line no-console
        console.log('[resumePollingIfNeeded] Already aborted before starting')
        return null
      }

      // Check current status
      // Extended timeout for long-running Playwright operations (120s)
      const statusRes = await api.get(
        `/api/agents/chat/sessions/${params.sessionId}/poll/`,
        { timeout: 120000 } // 2 minutes per poll request
      )

      const recommendedInterval = statusRes.data?.recommended_poll_interval_ms
      const jobStartedAt = statusRes.data?.job_started_at
      const initialJobStatus = statusRes.data?.job_status

      // Backend signals no polling needed (null = stop)
      if (recommendedInterval === null) {
        return null
      }

      // Active job found - resume polling
      // Pass start time from backend if available (let caller handle fallback)
      const startTime = jobStartedAt
        ? new Date(jobStartedAt).getTime()
        : undefined
      params.onProgress?.(PROCESSING_MESSAGES.RESUMING)
      // Set initial status based on backend job_status
      const initialStatus =
        initialJobStatus === 'started'
          ? PROCESSING_STATUS.PROCESSING
          : PROCESSING_STATUS.QUEUED
      params.onStatusChange?.(initialStatus, startTime)

      let attempts = 0
      const maxAttempts = 60
      let currentPollInterval = 2000

      while (attempts < maxAttempts) {
        // Check if polling was aborted
        if (params.abortSignal?.aborted) {
          // eslint-disable-next-line no-console
          console.log('[resumePollingIfNeeded] Aborted, returning null')
          params.onToolProgress?.(null)
          return null
        }

        try {
          // Extended timeout for long-running Playwright operations (120s)
          const pollRes = await api.get(
            `/api/agents/chat/sessions/${params.sessionId}/poll/`,
            { timeout: 120000 } // 2 minutes per poll request
          )

          const status = pollRes.data?.job_status
          // toolProgress disabled for now - will be re-enabled when UI is redesigned
          // const toolProgress = pollRes.data?.tool_progress
          const result = pollRes.data?.result
          const agentMetadata = pollRes.data?.agent_metadata
          const recommendedInterval = pollRes.data?.recommended_poll_interval_ms

          // Check if job completed FIRST (before checking poll interval)
          if (status === 'completed') {
            const data = result?.data || result
            const response = String(data?.response ?? '')
            const title = data?.title ? String(data.title) : undefined
            params.onToolProgress?.(null)

            return {
              response,
              title,
              agentMetadata: agentMetadata as Record<string, unknown>,
            }
          }

          if (status === 'error') {
            params.onToolProgress?.(null)
            throw new Error(
              String(
                pollRes.data?.error ||
                  result?.error ||
                  'Não consegui processar sua mensagem. Tente de novo.'
              )
            )
          }

          // Backend signals to STOP polling (null = no more polling needed)
          if (recommendedInterval === null) {
            return null
          }

          if (
            recommendedInterval &&
            recommendedInterval !== currentPollInterval
          ) {
            currentPollInterval = recommendedInterval
          }

          // Update UI based on job status (pending = queued, started = processing)
          if (status === 'pending') {
            params.onStatusChange?.(PROCESSING_STATUS.QUEUED)
            params.onProgress?.(PROCESSING_MESSAGES.QUEUED)
          } else if (status === 'started') {
            params.onStatusChange?.(PROCESSING_STATUS.PROCESSING)
            params.onProgress?.(PROCESSING_MESSAGES.PROCESSING)
          }

          const interval = recommendedInterval || currentPollInterval
          await new Promise(resolve => setTimeout(resolve, interval))
          attempts++
        } catch (error: unknown) {
          if (
            error &&
            typeof error === 'object' &&
            'response' in error &&
            (error as { response?: { status?: number } }).response?.status ===
              202
          ) {
            await new Promise(resolve =>
              setTimeout(resolve, currentPollInterval)
            )
            attempts++
            continue
          }
          throw error
        }
      }

      throw new Error('Timeout waiting for response')
    } catch {
      // If polling check fails, just return null (no active job)
      return null
    }
  },
}

export default chatApi
